import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Download, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useParticipant } from "@/context/ParticipantContext";
import { CERTIFICATE } from "@/constants/testIds";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_60dfbe01-e8f1-47bb-9c72-2a0d72443632/artifacts/waarlbny_DENSO-main-logo-web.jpg";

export default function Certificate() {
  const { participant, progress, refreshProgress } = useParticipant();
  const navigate = useNavigate();
  const certRef = useRef(null);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    refreshProgress();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (progress && !progress.certified) {
      navigate("/quiz");
    }
  }, [progress, navigate]);

  const dateStr = new Date().toLocaleDateString("en-ZA", { year: "numeric", month: "long", day: "numeric" });

  const handleDownload = async () => {
    if (!certRef.current) return;
    setDownloading(true);
    try {
      const canvas = await html2canvas(certRef.current, { scale: 2, backgroundColor: "#ffffff" });
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF({ orientation: "landscape", unit: "px", format: [canvas.width, canvas.height] });
      pdf.addImage(imgData, "PNG", 0, 0, canvas.width, canvas.height);
      pdf.save(`DENSO_Certificate_${participant?.name}_${participant?.surname}.pdf`);
    } finally {
      setDownloading(false);
    }
  };

  if (!participant || !progress?.certified) {
    return <div className="max-w-2xl mx-auto px-4 py-20 text-center text-slate-500">Loading certificate...</div>;
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-10 space-y-6">
      <button
        onClick={() => navigate("/home")}
        data-testid={CERTIFICATE.backButton}
        className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-denso-red transition-colors"
      >
        <ArrowLeft className="h-4 w-4" /> Back to Home
      </button>

      <motion.div
        initial={{ opacity: 0, scale: 0.97 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.4 }}
      >
        <div
          ref={certRef}
          data-testid={CERTIFICATE.container}
          className="bg-white p-10 sm:p-14 relative"
          style={{ border: "10px solid #D3000F" }}
        >
          <div className="absolute inset-3 border border-[#ED1B24]" />
          <div className="relative z-10 text-center space-y-6">
            <img src={LOGO_URL} alt="DENSO" className="h-14 mx-auto rounded-sm" />
            <p className="text-xs uppercase tracking-[0.3em] text-slate-400 font-semibold">Certificate of Completion</p>
            <h1 className="font-heading text-3xl sm:text-4xl font-bold text-[#D3000F]">
              DENSO Product Training Course
            </h1>
            <p className="text-slate-500 text-sm">This certificate is proudly presented to</p>
            <h2 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900 border-b-2 border-slate-200 inline-block pb-2 px-8">
              {participant.name} {participant.surname}
            </h2>
            <p className="text-slate-600 text-sm max-w-xl mx-auto leading-relaxed">
              For successfully completing the DENSO Product Training Programme and achieving a
              passing score of <span className="font-bold text-[#D3000F]">{progress?.certificate_score_percentage ?? 90}%</span> in the Q&A
              Assessment, demonstrating comprehensive knowledge of DENSO products and services.
            </p>
            <div className="flex items-center justify-center gap-10 pt-4 text-sm">
              <div>
                <p className="font-semibold text-slate-800">{participant.branch}</p>
                <p className="text-slate-400 text-xs uppercase tracking-wide mt-1">Branch / Location</p>
              </div>
              <div>
                <p className="font-semibold text-slate-800">{dateStr}</p>
                <p className="text-slate-400 text-xs uppercase tracking-wide mt-1">Date Issued</p>
              </div>
              <div>
                <p className="font-semibold text-slate-800">{progress.certificate_id}</p>
                <p className="text-slate-400 text-xs uppercase tracking-wide mt-1">Certificate ID</p>
              </div>
            </div>
            <p className="text-xs text-slate-400 pt-4">DENSO Training Team &mdash; Distributor & Product Training Programme</p>
          </div>
        </div>
      </motion.div>

      <div className="flex justify-center">
        <Button
          data-testid={CERTIFICATE.downloadButton}
          onClick={handleDownload}
          disabled={downloading}
          className="bg-denso-red hover:bg-denso-lightred text-white h-11 px-6"
        >
          <Download className="h-4 w-4" /> {downloading ? "Generating..." : "Download Certificate (PDF)"}
        </Button>
      </div>
    </div>
  );
}
